import { Center, Group, Loader, Stack, Text } from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import { useLanguage } from "../../../../../../../stores/LanguageStore";
import { useLeadsSheetStore } from "../../../../../../../stores/LeadsStore";
import { findProspect } from "../../../../../../../api/PRMAPI";
import AnswersGroup from "./AnswersGroup";
import ConversationStart from "./email/ConversationStart";
import EmailSenderModal from "./email/EmailSenderModal";
import { groupByMonthAndProgId } from "../../../../utils";

const TabAnswers = () => {
  const lang = useLanguage((s) => s.language);
  const current = useLeadsSheetStore((s) => s.current);
  const setCurrent = useLeadsSheetStore((s) => s.setCurrent);
  const currentContainer = useLeadsSheetStore((s) => s.currentContainer);
  const setSheetLoading = useLeadsSheetStore((s) => s.setLoading);
  const [prospect, setProspect] = useState(current);
  const [loading, setLoading] = useState(false);

  const about = useMemo(() => {
    const aboutList = [
      { key: "%last_name%", value: "lastName" },
      { key: "%first_name%", value: "firstName" },
      { key: "%job_title%", value: "jobTitle" },
      { key: "%company%", value: "company" },
      { key: "%email%", value: "email" },
    ];
    let result = {};
    aboutList.forEach((item) => {
      result[item.value] =
        current?.properties?.find((x) => x.identifier_placeholder === item.key)
          ?.value || " - ";
    });

    return result;
  }, [current]);

  const items = useMemo(() => {
    if (!current?.programmations && !current?.history) {
      return [];
    }

    const programmations = current?.programmations || [];
    const history = current?.history || [];

    const result = [];
    const unlinkedItems = [];

    for (const prog of programmations) {
      const matchedItems = history.filter((x) => {
        return (
          x?.workflow_programmation_id === prog.programmation_id &&
          (x.history_type === "sent" ||
            x.history_type === "reply" ||
            x?.event === "email_sent")
        );
      });

      result.push({
        ...prog,
        items: matchedItems,
      });
    }

    // Find unlinked items (no matching programmation_id)
    for (const x of history) {
      if (
        (x.history_type === "sent" || x.history_type === "reply") &&
        !x?.workflow_programmation_id
      ) {
        unlinkedItems.push(x);
      }
    }

    // Add a synthetic "Unlinked" group if needed
    if (unlinkedItems.length > 0) {
      result.push({
        programmation_id: null,
        name: "Unlinked Activities",
        items: unlinkedItems,
      });
    }

    return groupByMonthAndProgId(result);
  }, [current]);

  useEffect(() => {
    if (current?.id !== prospect?.id) setLoading(true);
  }, [current?.id, prospect?.id]);

  useEffect(() => {
    if (loading) {
      findProspect(current.id)
        .then((response) => {
          setProspect(response.data.contact_profile);
          setCurrent({
            ...current,
            history: response.data.contact_profile.history,
          });
        })
        .finally(() => setLoading(false));
    }
  }, [current.id, loading]);

  return (
    <Stack
      gap={0}
      flex={1}
      h={0}
      mih={"100%"}
      style={{ overflow: "auto" }}
      p={"lg"}
      px={0}
    >
      <Group mb={"xs"} justify="end">
        <EmailSenderModal
          current={current}
          onSent={() => setSheetLoading(true)}
        />

        <ConversationStart />
      </Group>

      {loading ? (
        <Center h={"calc(100% - 103px)"}>
          <Loader type="oval" size={48} />
        </Center>
      ) : items.length > 0 ? (
        <Stack h={"100%"} mb={"sm"}>
          {items.map((item, index) => (
            <AnswersGroup
              groupIndex={index}
              key={index}
              about={about}
              campaign={item}
              currentContainer={currentContainer}
              prospect={prospect}
              setLoading={setLoading}
              setProspect={setProspect}
            />
          ))}
        </Stack>
      ) : (
        <Text c={"dimmed"} mt={"xs"}>
          {lang.statistics.responses_viewer.no_answers}
        </Text>
      )}
    </Stack>
  );
};

export default TabAnswers;
